import { useQuery } from '@tanstack/react-query';

import { getPrintProgress } from '@/services/main/rezept/rezeptListService';
import type { RezeptPrintProgress } from '@/types/rezept.types';

import { getStoredClinicContext } from './useClinicContext';

/** Message shown when the job row backing this token is gone (progress returns 404). */
export const REZEPT_PDF_PROGRESS_UNREACHABLE_MESSAGE =
  '印刷ジョブが見つかりませんでした。既に表示済みか、処理が中断された可能性があります。再度レセプト発行を実行してください。';

/** Message shown when progress cannot be read for any other reason. */
export const REZEPT_PDF_PROGRESS_ERROR_MESSAGE =
  '印刷処理の状態を取得できませんでした。時間をおいてから再度実行してください。';

/**
 * Polled progress query for the レセプト発行 PDF generation tab.
 *
 * Uses `getStoredClinicContext()` (localStorage) instead of `useClinicContext()`
 * because the page runs without AuthProvider.
 *
 * Polls every 1 s until status is 'completed' or 'failed', or until the job can no
 * longer be read, then stops.
 */
export const useRezeptPdfProgress = (token: string | null) => {
  const ctx = getStoredClinicContext();

  const { data, isLoading, isError, error } = useQuery<RezeptPrintProgress>({
    queryKey: ['rezept-pdf-progress', token],
    queryFn: () => getPrintProgress(ctx!.tenant, ctx!.clinicNumber, token as string),
    enabled: !!token && !!ctx,
    // A job that dies mid-run (sync queue: the request process is the worker) never
    // reaches its completed/failed update, and its row is later purged by the stale-job
    // cleanup in RezeptIssuePdfService::startJob(). Progress then 404s forever — that is
    // terminal, so fail fast on it and only retry the transient failures.
    retry: (failureCount, err) => !isJobGone(err) && failureCount < 2,
    refetchInterval: query => {
      const d = query.state.data as RezeptPrintProgress | undefined;
      if (d?.status === 'completed' || d?.status === 'failed') return false;
      // Stop once the job is unreadable. Without this the popup polls every second
      // forever and sits on 印刷処理中 with no way for the user to tell it is dead.
      if (query.state.status === 'error') return false;
      return 1000;
    },
    // Keep polling while the tab is hidden. react-query's focusManager reports
    // `document.visibilityState !== 'hidden'`, which a separate popup window satisfied even
    // when it was not focused — a background *tab* does not. Without this, going back to the
    // レセプト発行 tab froze the job's progress until the user switched back.
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 0,
  });

  const progressError = isError
    ? getProgressErrorMessage(error)
    : null;

  return {
    status: data?.status ?? 'pending',
    completedCount: data?.completed_count ?? 0,
    totalCount: data?.total_count ?? 0,
    isComplete: data?.status === 'completed',
    isFailed: data?.status === 'failed',
    errorMessage: data?.error_message ?? progressError,
    failedRezeptIds: data?.failed_rezept_ids ?? null,
    isLoading,
    isError,
  };
};

/** The job row backing this token no longer exists — nothing to wait for. */
function isJobGone(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 404;
}

function getProgressErrorMessage(error: unknown): string {
  return isJobGone(error)
    ? REZEPT_PDF_PROGRESS_UNREACHABLE_MESSAGE
    : REZEPT_PDF_PROGRESS_ERROR_MESSAGE;
}

export default useRezeptPdfProgress;
